import { Box, Stack, Typography } from '@mui/material'
import { GRAY_1000, GRAY_900 } from 'constants/styling/theme'
import { InvoicingAddressDTO, useDeleteInvoicingAddress } from 'dataQueries/invoicingAddresses.query'

import { FC } from 'react'
import { MUIButton } from 'components/common/MUIButton'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { VatValidationStatusBadge } from 'components/common/Badges'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'

/**
 * Props for {@link InvoicingAddressItem}
 */
interface Props {
  /** Full invoicing address DTO to be displayed */
  address: InvoicingAddressDTO

  /**
   * Callback fired when user clicks the "Edit" button.
   * @param addressId - Identifier of the selected address
   */
  onEdit: (addressId: string) => void
}

/**
 * Displays a single invoicing address item inside a bordered container,
 * including address details and edit/delete actions.
 *
 * @remarks
 * Uses {@link SectionedBorderBox} to visually separate the address
 * and {@link useDeleteInvoicingAddress} mutation for deletion logic.
 *
 * This component is purely presentational except for invoking the delete mutation.
 *
 * @component
 * @example
 * ```tsx
 * <InvoicingAddressItem
 *   address={address}
 *   onEdit={(id) => setSelectedAddress(id)}
 * />
 * ```
 */
export const InvoicingAddressItem: FC<Props> = ({
  address,
  onEdit,
}) => {
  const { spawnSuccessToast, spawnErrorToast } = useSnackbar()

  /** Provides translations for common labels like "Edit" and "Delete". */
  const { t, i18n } = useTranslation(['common', 'country', 'invoicing_address_form'])

  /** Delete mutation for removing the invoicing address. */
  const deleteAddressMutation = useDeleteInvoicingAddress()

  return (
    <SectionedBorderBox
      elevation="md"
      title={
        <Stack
          direction="row"
          gap={2}
          justifyContent="space-between"
          width="100%"
          alignItems="center"
        >
          {/* Address display name */}
          <Box flex="1 1 auto">
            <Typography variant="text-md" fontWeight={500} color={GRAY_1000} sx={{ overflowWrap: 'anywhere' }}>
              {address.addressName}
            </Typography>
          </Box>

          {/* Action buttons */}
          <Stack direction="row" gap={1} flex="0 0 auto">
            <MUIButton
              type="secondaryBorder"
              onClick={() => onEdit(address.id)}
            >
              {t('common:Edit')}
            </MUIButton>

            <MUIButton
              type="orangeSecondaryBorder"
              onClick={() => deleteAddressMutation.mutate(
                { addressId: address.id },
                {
                  onSuccess: () => spawnSuccessToast(t('invoicing_address_form:delete_success')),
                  onError: (e) => spawnErrorToast(t('invoicing_address_form:delete_success'), { error: e?.request })
                }
              )}
              isLoading={deleteAddressMutation.isPending}
            >
              {t('common:Delete')}
            </MUIButton>
          </Stack>
        </Stack>
      }
    >
      {/* Address details */}
      <Stack gap={0.1}>
        {/* Company or individual name */}
        <Typography fontWeight={500} color={GRAY_900}>
          {address.companyOrIndividualName}
        </Typography>

        {/* Street info */}
        <Typography>
          {address.streetAndNumber}
        </Typography>

        {/* Combined location line */}
        <Typography>
          {[
            address.apartmentNumber,
            address.city,
            address.region,
            address.postalCode,
            i18n.exists(`country:${address.country}`) ? t(`country:${address.country}`) : address.country,
          ].filter(Boolean).join(', ')}
        </Typography>

        {/* VAT number */}
        {address.vatNumber && (
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography>
              {t('invoicing_address_form:vat_number')}:&nbsp;
              <Typography component="span" fontWeight={500} color={GRAY_900}>{address.vatNumber}</Typography>
            </Typography>
            <VatValidationStatusBadge
              status={address.validationStatus}
              vatNumber={address.vatNumber}
              country={address.country}
              size="sm"
            />
          </Stack>
        )}
      </Stack>
    </SectionedBorderBox>
  )
}
